import { useCallback } from 'react'
import ApiClient from 'api-client/ApiClient'
import { Subnet, INetworkSelector } from '../../networks/model'
import { withRetry } from 'utils/async'

export const ENABLE_SIMPLE_NETWORK_BATCH_CREATION = false

/** Map of networkId → portId */
export type PortsMap = Record<string, string>
export type SimpleNetworkPort = PortsMap

/** Ports pre-created for a single VM, split by network type */
export type NetworkPortsResult = { simple: PortsMap; managed: PortsMap }

export const useNetworkPorts = () => {
  const getNeutron = () => ApiClient.getInstance().neutron

  const deletePort = async (portId: string): Promise<void> => {
    try {
      await getNeutron().deletePort(portId)
      console.log(`[Port] Deleted: ${portId}`)
    } catch (error) {
      console.error(`[Port] Delete failed: ${portId}`, error)
    }
  }

  const deletePorts = useCallback(async (ports: PortsMap): Promise<void> => {
    for (const portId of Object.values(ports)) {
      await deletePort(portId)
    }
  }, [])

  /**
   * Create ports for simple networks (L2-only) for a single VM in parallel with retry.
   */
  const createSimplePortsForVM = async (
    simpleNetworks: INetworkSelector[],
    tenantId: string,
    vmName: string,
    vmIndex?: number,
  ): Promise<PortsMap> => {
    const portResults = await Promise.all(
      simpleNetworks.map((network) =>
        withRetry(
          async () => {
            const portName =
              vmIndex !== undefined
                ? `${vmName}-${vmIndex + 1}-${network?.name}-port`
                : `${vmName}-${network?.name}-port`
            const response = await getNeutron().createPort({
              port: {
                network_id: network.id,
                tenant_id: tenantId,
                admin_state_up: true,
                name: portName,
                port_security_enabled: false,
                'binding:profile': {
                  'l2-port': true,
                },
              },
            })

            const portId = response?.port?.id || response?.id
            if (!portId) throw new Error(`Port creation failed for ${network.name}`)

            console.log(`[Port] Created: ${network.name} → ${portId}`)
            return [network.id, portId] as const
          },
          2,
          5000,
        ),
      ),
    )

    return Object.fromEntries(portResults)
  }

  /**
   * Create ports for managed networks (with selected subnets) for a single VM in parallel with retry.
   */
  const createManagedPortsForVM = async (
    managedNetworks: INetworkSelector[],
    selectedSubnets: Record<string, Subnet>,
    tenantId: string,
    vmName: string,
    vmIndex?: number,
  ): Promise<PortsMap> => {
    const portResults = await Promise.all(
      managedNetworks.map((network) =>
        withRetry(
          async () => {
            const subnet = selectedSubnets[network.id]
            const portName =
              vmIndex !== undefined
                ? `${vmName}-${vmIndex + 1}-${network.name}-subnet-port`
                : `${vmName}-${network.name}-subnet-port`

            const response = await getNeutron().createPort({
              port: {
                network_id: network.id,
                fixed_ips: [{ subnet_id: subnet.id }],
                admin_state_up: true,
                tenant_id: tenantId,
                name: portName,
              },
            })

            const portId = response?.id
            if (!portId)
              throw new Error(
                `Port creation failed for network "${network.name}" on subnet "${subnet.name}" (${subnet.id})`,
              )

            console.log(
              `[Managed Port] Created: ${network.name} (subnet ${subnet.name}) → ${portId}`,
            )

            return [network.id, portId] as const
          },
          2,
          5000,
        ),
      ),
    )

    return Object.fromEntries(portResults)
  }

  /**
   * Pre-create ports for simple or managed networks in parallel with retry.
   * Note: VMs cannot mix simple and managed networks (enforced by UI), so only one type will be non-empty.
   * Handles single or batch VM creation.
   *
   * @param networks Full list of selected networks (all simple OR all managed, never both)
   * @param selectedSubnets Map of networkId → subnet (only for managed networks with subnet selection)
   * @param tenantId OpenStack tenant/project ID
   * @param vmName VM name (used to construct port names)
   * @param vmCount Number of VMs to create ports for (default 1)
   * @returns NetworkPortsResult if vmCount===1, NetworkPortsResult[] if vmCount>1
   */
  const preCreatePorts = useCallback(
    async (
      networks: INetworkSelector[],
      selectedSubnets: Record<string, Subnet> = {},
      tenantId: string,
      vmName: string,
      vmCount: number = 1,
    ): Promise<NetworkPortsResult | NetworkPortsResult[]> => {
      const simpleNetworks = networks.filter((n) => n.isSimpleNetwork)
      const managedNetworks = networks.filter((n) => !n.isSimpleNetwork && selectedSubnets[n.id])

      const empty: NetworkPortsResult = { simple: {}, managed: {} }

      // Nothing to create (no networks or no managed networks have subnet selection)
      if (simpleNetworks.length === 0 && managedNetworks.length === 0) {
        return vmCount === 1 ? empty : []
      }

      try {
        // Create ports for all VMs in parallel
        const vmPortResults = await Promise.allSettled(
          Array.from({ length: vmCount }, async (_, i) => {
            const name = vmCount > 1 ? `${vmName}-${i + 1}` : vmName
            const index = vmCount > 1 ? i : undefined

            // Create simple and managed ports in parallel (both within each VM)
            const [simple, managed] = await Promise.all([
              simpleNetworks.length > 0
                ? createSimplePortsForVM(simpleNetworks, tenantId, name, index)
                : Promise.resolve({}),
              managedNetworks.length > 0
                ? createManagedPortsForVM(managedNetworks, selectedSubnets, tenantId, name, index)
                : Promise.resolve({}),
            ])

            return { simple, managed }
          }),
        )

        // Check for failures and rollback all on any failure
        const allCreatedPorts: NetworkPortsResult[] = []
        for (const result of vmPortResults) {
          if (result.status === 'rejected') {
            console.error(
              '[Port] Creation failed, rolling back all created ports...',
              result.reason,
            )
            // Rollback all created ports on any failure
            for (const { simple, managed } of allCreatedPorts) {
              await deletePorts({ ...simple, ...managed })
            }
            throw result.reason
          }
          allCreatedPorts.push(result.value)
        }

        return vmCount === 1 ? allCreatedPorts[0] : allCreatedPorts
      } catch (error) {
        console.error('[Port] Pre-creation failed:', error)
        throw error
      }
    },
    [],
  )

  const hasSimpleNetworks = useCallback(
    (networks: INetworkSelector[]) => networks.some((n) => n.isSimpleNetwork),
    [],
  )

  const shouldEnableConfigDrive = useCallback(
    (networks: INetworkSelector[], cloudInitEnabled: boolean) =>
      cloudInitEnabled &&
      networks.some(
        (n) => n.isSimpleNetwork || n.subnetDetails?.some((s: any) => s.enable_dhcp === false),
      ),
    [],
  )

  return {
    preCreatePorts,
    cleanupPreCreatedPorts: deletePorts,
    hasSimpleNetworks,
    shouldEnableConfigDrive,
    ENABLE_SIMPLE_NETWORK_BATCH_CREATION,
  }
}

export default useNetworkPorts
